import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import StoreDialog, {
  FIELD_CLASS,
  FIELD_LABEL,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
} from "./StoreDialog";

const Footer = ({ children }) => (
  <div className="flex justify-end gap-2.5 border-t border-ui-border-subtle px-[16px] py-3.5 sm:px-[22px] [&>*]:flex-1 sm:[&>*]:flex-none">
    {children}
  </div>
);

const RenameForm = ({ store, saving, onSave, onClose }) => {
  const { t } = useTranslation("settings");
  const [name, setName] = useState(store.account_name ?? "");
  const trimmed = name.trim();
  const canSave = !!trimmed && trimmed !== store.account_name && !saving;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (canSave) onSave(store, trimmed);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col gap-0.5 px-[16px] sm:px-[22px] pb-1.5 pt-[20px]">
        <div className="text-[17px] font-semibold text-ui-text">
          {t("marketplaces.dialogs.renameTitle")}
        </div>
        <div className="text-[13px] text-ui-text-muted">
          {t("marketplaces.dialogs.renameHint")}
        </div>
      </div>
      <label
        className={`${FIELD_LABEL} px-[16px] sm:px-[22px] pb-[20px] pt-3.5`}
      >
        <span className="text-xs font-medium text-ui-text-secondary">
          {t("marketplaces.dialogs.storeName")}
        </span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
          className={`${FIELD_CLASS} h-10`}
        />
      </label>
      <Footer>
        <button type="button" onClick={onClose} className={SECONDARY_BUTTON}>
          {t("common:actions.cancel")}
        </button>
        <button type="submit" disabled={!canSave} className={PRIMARY_BUTTON}>
          {saving ? t("marketplaces.dialogs.saving") : t("common:actions.save")}
        </button>
      </Footer>
    </form>
  );
};

export const RenameStoreDialog = ({ store, saving, onSave, onClose }) => {
  const { t } = useTranslation("settings");
  return (
    <StoreDialog
      open={!!store}
      onClose={onClose}
      label={t("marketplaces.dialogs.renameTitle")}
    >
      {store && (
        <RenameForm
          key={store.id}
          store={store}
          saving={saving}
          onSave={onSave}
          onClose={onClose}
        />
      )}
    </StoreDialog>
  );
};

export const DisconnectStoreDialog = ({
  store,
  saving,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation("settings");
  return (
    <StoreDialog
      open={!!store}
      onClose={onClose}
      label={t("marketplaces.dialogs.disconnectLabel", {
        name: store?.account_name ?? t("marketplaces.dialogs.store"),
      })}
    >
      {store && (
        <>
          <div className="flex flex-col gap-2 px-[16px] sm:px-[22px] pb-[18px] pt-[22px]">
            <div className="text-[17px] font-semibold text-ui-text">
              {t("marketplaces.dialogs.disconnectTitle", {
                name: store.account_name,
              })}
            </div>
            <p className="m-[0px] text-[13px] leading-[1.6] text-state-neutral-text">
              {t("marketplaces.dialogs.disconnectBody")}
            </p>
          </div>
          <Footer>
            <button
              type="button"
              onClick={onClose}
              className={SECONDARY_BUTTON}
            >
              {t("marketplaces.dialogs.keepConnected")}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => onConfirm(store)}
              className="h-9 rounded-lg border-[1px] border-state-danger-solid bg-state-danger-solid px-[16px] text-[13px] font-medium text-brand-contrast hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving
                ? t("marketplaces.dialogs.disconnecting")
                : t("marketplaces.dialogs.disconnect")}
            </button>
          </Footer>
        </>
      )}
    </StoreDialog>
  );
};
